<template>
  <el-table 
    v-loading="loading"
    :class="['radio-table', `radio-table--${borderStyle}`, showExpand ? 'radio-table--expand' : '', isError ? 'radio-table--error' : '']"
    :max-height="height"
    :highlight-current-row="true"
    :data="showData"
    :row-class-name="rowClassName" 
    @row-click="handleRowClick"
  >
    <el-table-column 
      class-name="radio-table__radioColumn" 
      width="40" 
      align="center" 
      :label="radioLabel"
      :label-class-name="showExpand ? 'radio-table__label__expand' : ''"
    >
      <template slot="header" slot-scope="scope">
        <!-- 展开/收起 -->
        <div 
          v-if="showExpand" 
          :title="val ? (isExpand ? '收起' : '展开') : ''"
          :class="{ 'radio-table__label__expand__handle': true, 'is-expand': isExpand, 'is-disabled': !val }"
          @click.prevent="handleExpand"
        >
          <i class="el-icon-arrow-right"></i>
        </div>
        <template v-else>
          {{ radioLabel }}
        </template>
      </template>
      <template slot-scope="{ row }">
        <span :class="{ 'radio-table__radio': true, selected: row[radioKey] == val }"></span>
      </template>
    </el-table-column>
    <el-table-column
      v-for="(item, index) in columns"
      :key="item[radioKey]"
      :width="item.width || '—'"
      :min-width="item.minWidth || '—'"
      :prop="item.prop" 
      :label="item.label"
      :class-name="columnClassName(item, index)"
      :show-overflow-tooltip="item.showOverflowTooltip"
    >
      <!-- 表头插槽 -->
      <template slot="header" slot-scope="scope">
        <slot :name="item.headerSlot" v-bind="scope">
          {{ item.label }}
        </slot>
      </template>
      <!-- 内容插槽 -->
      <template slot-scope="{ row }">
        <div class="radio-table__content" :class="{ 'is-tooltip': item.showOverflowTooltip }">
          <slot v-if="item.slot" :name="item.slot" v-bind:row="row" />
          <template v-else>
            {{ item.formatter ? item.formatter(row) : row[item.prop] }}
          </template>
        </div>
      </template>
    </el-table-column>

    <template v-for="(slot, key) in $scopedSlots" :slot="key" slot-scope="scope">
      <slot :name="key" v-bind="scope"></slot>
    </template>
    <template v-for="(slot, key) in $slots" :slot="key">
      <slot :name="key"></slot>
    </template>
  </el-table>
</template>

<script>
export default {
  props: {
    value: {
      type: [Number, String],
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    // 表格线 solid-实线 dashed-虚线
    borderStyle: {
      type: String,
      default: 'solid'
    },
    height: {
      type: [Number, String],
      default: undefined
    },
    // 选中值的key
    radioKey: {
      type: String,
      default: ''
    },
    radioLabel: {
      type: String,
      default: ''
    },
    // 表头数据
    // { prop: xxx, label: xxx, width: xxx, minWidth: xxx, slot: xxx, formatter: row => {} }
    columns: {
      type: Array,
      default: []
    },
    tableData: {
      type: Array,
      default: []
    },
    // 是否显示展开/收起
    showExpand: {
      type: Boolean,
      default: false
    },
    // 是否显示错误样式
    isError: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      val: this.value,
      currnetRow: {},
      isExpand: true, // 是否是展开状态
    }
  },
  computed: {
    showData() {
      const { tableData, showExpand, isExpand, val, radioKey } = this
      // 有展开功能且收起状态时
      if (showExpand && !isExpand && val) {
        return tableData.filter(item => item[radioKey] == val)
      }
      return tableData
    }
  },
  watch: {
    value(newVal) {
      this.val = newVal
    }
  },
  methods: {
    handleEmit() {
      const { val, currnetRow } = this
      this.$emit('input', val)
      this.$emit('change', currnetRow)
    },
    handleRowClick(row) {
      this.val = row[this.radioKey]
      this.currnetRow = row
      this.handleEmit()
    },
    // 展开/收起
    handleExpand() {
      if (!this.showExpand || !this.val) return
      this.isExpand = !this.isExpand
    },
    // 行 class
    rowClassName({ row, rowIndex }) {
      const { val, radioKey } = this
      return row[radioKey] == val ? 'current-row' : ''
    },
    // 列 class
    columnClassName(item, index) {
      const { radioKey, columns } = this

      let className = ''
      if (item.prop === radioKey) {
        className = 'current-column'
      }
      if (index === columns.length - 1) {
        className += ' last-column'
      }
      if (index === 0) {
        className += ' first-data-column'
      }
      return className
    }
  }
}
</script>

<style lang="scss" scoped>

$border-color--solid: #E6E6E6;

.radio-table {
  // 实线
  &.radio-table--solid {
    border: 1px solid $border-color--solid;
    border-radius: $--border-radius-base2;
    &::before {
      height: 0;
    }
    // 覆盖表格样式
    ::v-deep {
      // 头部背景
      th.el-table__cell {
        color: #555555;
        background: #F9F9F9;
      }
      th.radio-table__radioColumn {
        color: #AAAAAA;
      }
      td.radio-table__radioColumn .cell {
        display: flex;
        justify-content: center;
        align-items: center;
      }
      // 收起/展开
      th.radio-table__label__expand {
        .cell {
          padding: 0;
        }
        .radio-table__label__expand__handle {
          cursor: pointer;
          &:not(.is-disabled):hover {
            color: $--color-text-important;
          }
          &.is-expand {
            >i {
              font-weight: 400;
              color: #AAAAAA;
              transform: rotate(90deg);
            }
          }
          &.is-disabled {
            cursor: not-allowed;
          }
          >i {
            font-size: 12px;
            font-weight: 700;
            color: $--form-color-base;
            transition: transform .2s;
          }
        }
      }
      .el-table__header {
        color: $--color-text-secondary;
      }
      .el-table__body {
        color: $--color-text-primary;
      }
      // hover背景
      tr:hover>td.el-table__cell,
      .current-row>td.el-table__cell {
        background: $--radio-checked-background-color;
      }
      // .current-row .current-column {
      //   font-weight: 700;
      // }
      .first-data-column {
        .cell {
          padding-left: 0;
        }
      }
      .el-table__cell {
        cursor: pointer;
        border-color: $border-color--solid;
      }
      // 表格高度
      .el-table__cell {
        padding: 0;
        &>.cell {
          line-height: 32px;
          font-size: 14px;
        }
      }
      // 底部的线
      .el-table__row:last-child td {
        border-bottom: 0;
      }
    }
  }
  // 虚线
  &.radio-table--dashed {
    padding: 5px;
    border: 1px dashed $--input-border-color;
    border-radius: 5px;
    &::before {
      display: none;
    }
    // 覆盖表格样式
    ::v-deep {
      // 头部背景
      th.el-table__cell {
        background: #fff;
      }
      thead {
        color: #555;
      }
      // hover背景
      tr:hover>td.el-table__cell,
      .current-row>td.el-table__cell {
        background: $--radio-checked-background-color;
      }
      .el-table__cell {
        cursor: pointer;
        border-bottom: 0;
      }
      th.el-table__cell {
        border-bottom: 1px dashed $--input-border-color;
      }
      // 表格高度
      .el-table__cell {
        padding: 0;
        &>.cell {
          line-height: 32px;
          font-size: 14px;
        }
      }
      // 底部的线
      .el-table__row:last-child td{
        border-bottom: 0;
      }
    }
  }
  &.radio-table--error {
    border-color: $--color-danger;
  }
  
  .radio-table__radio {
    box-sizing: border-box;
    position: relative;
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    border: 1px solid $--radio-border-color;
    &.selected {
      border-color: $--radio-checked-border-color;
      &::after {
        position: absolute;
        top: 3px;
        left: 3px;
        content: "";
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: $--radio-checked-font-color;
      }
    }
  }

  .radio-table__content.is-tooltip {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  ::v-deep {
    .el-table__header {
      .el-table__cell.gutter {
        border-bottom: 1px solid #EBEEF5;
      }
    }
    .el-table__body-wrapper {
      .el-table__body {
        width: 100% !important;
      }
      &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 3px;
        background-color: rgba(144,147,153,.5);
      }
    }
    .el-loading-mask {
      .el-loading-spinner {
        top: 50% !important;
        .path {
          stroke: $--form-color-base;
        }
      }
    }
  }
}
</style>